import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable, TablePagination } from '@frontend/app/refresh-components/DataTableV2';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Link } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '@frontend/shadcn/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@frontend/shadcn/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import { CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';

import { Tag } from '@frontend/app/refresh-components';
import { ProjectsRouteRoot } from '@frontend/app/containers/Projects/constants';
import { AUTOMATION_EXECUTION_TERMINABLE_STATUSES, AUTOMATION_EXECUTION_ENDED_STATUSES } from '@frontend/app/components/AutomationAuditTable/AutomationAuditTableTypes';
import { AutomationExecutionStatus } from '@services/automations/types/AutomationExecution';
import { ListExecutions_listExecutions_results, ListExecutions_listExecutions_results_variables } from '@frontend/app/queries/types/ListExecutions';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { format } from 'date-fns';
import { XmarkIcon } from '@revfluence/fresh-icons/regular/esm';
import { SearchOutlined } from '@ant-design/icons';
import { Input } from '@frontend/shadcn/components/ui/input';
import { getFailedNodeType } from '@frontend/app/components/automationV2/utils/nodeIconMapping';
import { useMessagingContext } from '@/hooks';
import { useListExecutions, useTerminateAutomationExecutionMutation } from '@/app/hooks/automations';
import { useGetMembersByIdsQuery } from '@/app/hooks';
import { GetMemberQuery_member } from '@/app/queries/types/GetMemberQuery';
import { AutomationProvidersEnum, AutomationVariableType } from '@/app/types/globalTypes';
import { getAutomationExecutionStatus } from '../utils/automationUtils';
import { AutomationExecution } from '../constants';

// Constants
const TABLE_PAGE_SIZE = 200;

interface PagingInfo {
  totalCount: number;
  offset: number;
  limit: number;
  count: number;
  hasMore: boolean;
}

type TableColumnDef = ColumnDef<AutomationExecution> & {
  accessorKey?: string;
  meta?: {
    label: string;
  };
};

// Utils
function getMemberIdFromAutomationExecution(execution: ListExecutions_listExecutions_results): number | undefined {
  const member = execution?.variables?.find((variable: ListExecutions_listExecutions_results_variables) => variable.type === AutomationVariableType.MEMBER);
  return member ? parseInt(member.value, 10) : undefined;
}

function listExecutionToAutomationExecutionAdapter(
  execution: ListExecutions_listExecutions_results,
  member?: GetMemberQuery_member,
): AutomationExecution {
  return {
    automation: execution?.automation,
    member,
    status: execution.status,
    dateTriggered: execution.createdDate,
    dateEnded: AUTOMATION_EXECUTION_ENDED_STATUSES.includes(execution.status) ? execution.updatedDate : null,
    id: execution.id,
    metadata: execution.metadata,
  };
}

type TProps = {
  projectId: number;
  isStaOffer?: boolean;
  baseUri?: string;
};

// Add CancelDialog component before the main component
interface CancelDialogProps {
  execution: AutomationExecution;
  onTerminate: (executionId: string) => Promise<void>;
  onSuccess: () => void;
}

function CancelDialog({ execution, onTerminate, onSuccess }: CancelDialogProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleTerminate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onTerminate(execution.id);
      onSuccess();
      setIsOpen(false);
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button size="xs" variant="destructive">
          Cancel
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to cancel this automation run?</AlertDialogTitle>
          <AlertDialogDescription>
            {`${execution.member?.name} will be removed from the ${execution.automation.name} automation and the action will not be performed.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <form onSubmit={handleTerminate}>
            <Button type="submit">Continue</Button>
          </form>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function AutomationExecutionTable({ projectId, isStaOffer, baseUri }: TProps) {
  // State
  const [pageIndex, setPageIndex] = useState(0);
  const [terminateAutomationExecution] = useTerminateAutomationExecutionMutation();
  const { showSuccessMessage, showErrorMessage } = useMessagingContext();
  const { automationV2: isAutomationV2Enabled } = useClientFeatures();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  // Debounce search text
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  // Data fetching
  const {
    loading: isLoading,
    data: { listExecutions: { results: executions = [], paging } } = {
      listExecutions: {
        results: [],
        paging: { totalCount: 0 } as PagingInfo,
      },
    },
    refetch: refetchExecutions,
  } = useListExecutions({
    variables: {
      paging: {
        offset: pageIndex * TABLE_PAGE_SIZE,
        limit: TABLE_PAGE_SIZE,
      },
      filters: {
        automation: {
          program: {
            id: !isStaOffer ? projectId : undefined,
          },
          providerId: isStaOffer ? projectId : undefined,
          providerName: isStaOffer ? AutomationProvidersEnum.STA_OFFER_EMAILS : undefined,
        },
        search: debouncedSearchText,
      },

    },
    fetchPolicy: 'no-cache',
  });
  // Fetch member data for executions
  const memberIds = executions.map(getMemberIdFromAutomationExecution).filter(Boolean) as number[];
  const { data: { members: membersByIds = [] } = {} } = useGetMembersByIdsQuery(memberIds, {
    skip: isLoading || !memberIds.length,
    fetchPolicy: 'no-cache',
  });

  // Adapt executions with member data
  const data = useMemo(
    () =>
      executions.map((execution) => {
        const member = membersByIds.find(
          (member) => member.id === getMemberIdFromAutomationExecution(execution),
        );
        return listExecutionToAutomationExecutionAdapter(execution, member);
      }),
    [executions, membersByIds],
  );

  const handleTerminateExecution = useCallback(async (executionId: string) => {
    try {
      await terminateAutomationExecution({
        variables: {
          executionId,
          reason: 'User Initiated',
        },
      });
      showSuccessMessage('Automation execution terminated successfully');
      refetchExecutions();
    } catch (error) {
      showErrorMessage('Failed to terminate automation execution');
    }
  }, [terminateAutomationExecution, showSuccessMessage, showErrorMessage, refetchExecutions]);

  // Columns
  const columns = useMemo<TableColumnDef[]>(
    () => [
      {
        accessorKey: 'dateTriggered',
        header: 'Date Triggered',
        meta: { label: 'Date Triggered' },
        cell: ({ row }) => {
          const dateTriggered = row.getValue('dateTriggered') as string | null;
          if (!dateTriggered) return '-';
          const date = new Date(dateTriggered);
          if (isNaN(date.getTime())) return '-';
          return format(date, 'MMM d, yyyy');
        },
        size: 200,
        minSize: 200,
      },
      {
        accessorKey: 'dateEnded',
        header: 'Date Ended',
        meta: { label: 'Date Ended' },
        cell: ({ row }) => {
          const dateEnded = row.getValue('dateEnded') as string | null;
          if (!dateEnded) return '-';
          const date = new Date(dateEnded);
          if (isNaN(date.getTime())) return '-';
          return format(date, 'MMM d, yyyy');
        },
        size: 200,
        minSize: 200,
      },
      {
        accessorKey: 'automationName',
        header: 'Automation Name',
        meta: { label: 'Automation Name' },
        cell: ({ row }) => (
          <Button className="text-primary underline group hover:text-primary m-0 p-0" variant="link" asChild>
            <Link
              to={
                isAutomationV2Enabled
                  ? isStaOffer ? `${baseUri}/offers/${projectId}/offer_automation/${row.original.automation.id}?automationId=${row.original.automation.id}` : `${ProjectsRouteRoot}/${projectId}/automation_v2/automation/${row.original.automation.id}`
                  : `${ProjectsRouteRoot}/${projectId}/automation_detail/automation/${row.original.automation.id}`
              }
            >
              {row.original.automation.name}
            </Link>
          </Button>
        ),
        size: 200,
        minSize: 200,
      },
      {
        accessorKey: 'member',
        header: 'Member',
        meta: { label: 'Member' },
        cell: ({ row }) => {
          const name = row.original.member?.name;
          const initials = name
            ?.split(' ')
            .map((n) => n[0]?.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, ''))
            .join('');
          if (!row?.original?.member) {
            return 'N/A';
          }
          return (
            <Link className="text-primary group hover:text-primary" to={`/members/${row.original.member?.id}`}>
              <div className="flex items-center gap-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage
                    src={row.original.member?.profilePicture}
                    alt={row.original.member?.name}
                    className="object-cover"
                  />
                  <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                </Avatar>
                <div className="text-sm font-medium underline group-hover:underline">{row.original.member?.name}</div>
              </div>
            </Link>
          );
        },
        size: 200,
        minSize: 200,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        meta: { label: 'Status' },
        cell: ({ row }) => {
          const execution = row.original;
          const isFailed = execution.status === 'FAILED';
          const failureReason = execution.metadata?.failureReason;
          const errorMetadata = execution.metadata?.errorMetadata;
          const failedNode = getFailedNodeType(errorMetadata);

          return (
            <div className="flex items-center">
              <Tag
                label={getAutomationExecutionStatus(row.original.status)?.text}
                bgColor={getAutomationExecutionStatus(row.original.status)?.bg}
                textColor="text-grey-6"
              />
              {isFailed && failureReason && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <CircleInfoIcon className="text-red-5 ml-2 cursor-help" fontSize={14} />
                    </TooltipTrigger>
                    <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-2xl px-3 py-2 font-medium z-50">
                      <div className="max-w-sm">
                        <p className="font-medium mb-1">Error Details:</p>
                        {failedNode && (
                          <p className="text-sm text-red-600 mb-1">Failed at: {failedNode} node</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">{failureReason}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        },
        size: 120,
        minSize: 120,
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        meta: { label: 'Actions' },
        cell: ({ row }) => {
          return AUTOMATION_EXECUTION_TERMINABLE_STATUSES.includes(row.original.status as AutomationExecutionStatus) ? (
            <CancelDialog
              execution={row.original}
              onTerminate={handleTerminateExecution}
              onSuccess={() => refetchExecutions()}
            />
          ) : null;
        },
        size: 100,
        minSize: 100,
      },
    ],
    [projectId, handleTerminateExecution, refetchExecutions, isAutomationV2Enabled, baseUri, isStaOffer],
  );

  // Table component
  const tableComponent = useMemo(
    () => (
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        enableColumnResizing
        className="h-full"
        removeBorderBottom
      />
    ),
    [columns, data, isLoading],
  );

  // Reset pagination when search changes
  useEffect(() => {
    if (pageIndex > 0) {
      setPageIndex(0);
    }
  }, [debouncedSearchText, pageIndex]);

  // Calculate pagination range and next page logic using paging object
  const offset = paging?.offset ?? 0;
  const limit = paging?.limit ?? TABLE_PAGE_SIZE;
  const count = paging?.count ?? executions.length;
  const hasMore = paging?.hasMore ?? false;

  // Render
  return (
    <div className="w-full border-grey-2 rounded-2xl overflow-hidden border-solid border-[1px] flex flex-col">
      <div className="w-full bg-white flex justify-between items-center px-4 py-2 border-b border-grey-2">
        <div className="text-sm text-muted-foreground">
          {paging?.count ?? executions.length} Events{debouncedSearchText ? ' (filtered)' : ''}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex items-center">
            <Button
              variant="ghost"
              className="w-8 h-8 flex items-center justify-center z-20"
              size="icon"
              onClick={() => {
                setIsSearchOpen((v) => !v);
                if (isSearchOpen) {
                  setSearchText('');
                }
              }}
              aria-label={isSearchOpen ? 'Close search' : 'Open search'}
              aria-expanded={isSearchOpen}
              aria-controls="automation-execution-search-input"
            >
              {isSearchOpen ? (
                <XmarkIcon className="text-muted-foreground" />
              ) : (
                <SearchOutlined className="text-muted-foreground" />
              )}
            </Button>
            <div
              className={`absolute right-10 top-0 transition-all duration-300 z-10 ${isSearchOpen ? 'opacity-100 w-[18rem]' : 'opacity-0 w-0'} overflow-hidden`}
            >
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                type="search"
                placeholder="Search by name, member, status..."
                className="rounded-lg bg-background pl-8 w-[18rem] focus:ring-0 focus-visible:ring-0 outline-none"
                autoFocus={isSearchOpen}
              />
              <SearchOutlined className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            </div>
          </div>
          <TablePagination
            pageIndex={Math.floor(offset / limit)}
            pageSize={limit}
            totalCount={paging?.count ?? count}
            canPreviousPage={offset > 0}
            canNextPage={hasMore}
            onPaginationChange={(newPageIndex) => setPageIndex(newPageIndex)}
          />
        </div>
      </div>
      <div className="flex-1 overflow-hidden">{tableComponent}</div>
    </div>
  );
}
